import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { fadeUp, staggerContainer, viewportOnce } from "@/lib/animations";

const Counter = ({ from = 0, to, suffix = "", duration = 1.6, decimals = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [val, setVal] = useState(from);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, from, to, duration]);

  return (
    <span ref={ref}>
      {decimals ? val.toFixed(decimals) : Math.round(val)}
      {suffix}
    </span>
  );
};

const metrics = [
  { label: "OTD Improvement", from: 35, to: 95, suffix: "%", note: "Up from 35% baseline" },
  { label: "Faster Quoting", from: 0, to: 60, suffix: "%", note: "Excel tool standardization" },
  { label: "Years of Delivery", from: 0, to: 13, suffix: "+", note: "Industrial & engineering programs" },
  { label: "Countries Served", from: 0, to: 9, suffix: "", note: "DE · IT · UK · ES · SG · BR · IN · US · FR" },
];

export const Results = () => {
  return (
    <section id="results" className="relative py-24 md:py-32 bg-[#051024]" data-testid="results-section">
      <div className="px-6 md:px-12 lg:px-24">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="max-w-3xl mb-16"
        >
          <motion.div variants={fadeUp} className="flex items-center gap-4 mb-6">
            <div className="gold-rule" />
            <span className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Impact</span>
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-serif text-4xl md:text-5xl text-[#F8F9FA] leading-tight text-balance" data-testid="results-heading">
            Numbers speak. <em className="text-[#D4AF37] font-medium">Outcomes</em> hold.
          </motion.h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#233554]"
        >
          {metrics.map((m) => (
            <motion.div
              key={m.label}
              variants={fadeUp}
              className="border-b border-r border-[#233554] p-8 md:p-10 hover:bg-[#0A192F] transition-colors"
              data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <p className="font-serif text-5xl md:text-6xl text-[#D4AF37] leading-none tracking-tight">
                <Counter from={m.from} to={m.to} suffix={m.suffix} />
              </p>
              <p className="mt-4 text-sm uppercase tracking-[0.25em] text-[#F8F9FA]">{m.label}</p>
              <p className="mt-2 text-sm text-[#A8B2D1]">{m.note}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.7 }}
          className="mt-16 border border-[#D4AF37]/40 bg-gradient-to-br from-[#0A192F] to-[#051024] p-10 md:p-14 grid md:grid-cols-3 gap-8 items-center"
          data-testid="award-callout"
        >
          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Recognition</p>
            <h3 className="font-serif text-2xl md:text-3xl text-[#F8F9FA] leading-snug">
              "Customer Hero" award — recognized by{" "}
              <span className="text-[#D4AF37]">Emerson</span> for outstanding delivery to global customers.
            </h3>
          </div>
          <div className="font-serif text-7xl md:text-8xl text-[#D4AF37]/30 text-right leading-none">★</div>
        </motion.div>
      </div>
    </section>
  );
};
